import React, { useEffect, useRef, useState } from "react";
import {
  X,
  Upload,
  FileSignature,
  Calendar,
  Building2,
  Hash,
  AlertCircle,
  FileText,
  PenLine,
  Trash2,
  Send,
  Save,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import {
  createSigningRequest,
  submitSigningRequest,
} from "../../../api/hrm/signing/signingRequests";
import { showErrorToast, showSuccessToast } from "../../../components/ConfirmDialog";
import {
  SIGNER_ROLE_OPTIONS,
  DOCUMENT_TYPE_OPTIONS,
  URGENCY_OPTIONS,
} from "./signingConstants";

const labelClass =
  "text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1 flex items-center gap-1.5";
const inputClass =
  "w-full mt-1.5 bg-gray-50 border border-gray-200 rounded-2xl px-4 py-3 text-sm font-medium text-gray-700 outline-none focus:bg-white focus:border-red-500 focus:ring-4 focus:ring-red-500/5 transition-all";
const sectionClass =
  "rounded-2xl border border-gray-100 bg-gradient-to-br from-gray-50/80 to-white p-5 space-y-4";

const todayISO = () => new Date().toISOString().split("T")[0];

const isPdfFile = (f) =>
  f &&
  (f.type === "application/pdf" ||
    String(f.name || "").toLowerCase().endsWith(".pdf"));

const SigningRequestCreateModal = ({ open, onClose, onCreated }) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [documentType, setDocumentType] = useState("quyet_dinh");
  const [documentNumber, setDocumentNumber] = useState("");
  const [issuedDate, setIssuedDate] = useState(todayISO());
  const [effectiveUntil, setEffectiveUntil] = useState("");
  const [urgency, setUrgency] = useState("normal");
  const [issuingDepartment, setIssuingDepartment] = useState("");
  const [signerRole, setSignerRole] = useState("COO");
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitMode, setSubmitMode] = useState(null); // "draft" | "submit"
  const fileInputRef = useRef(null);
  const dragDepthRef = useRef(0);

  useEffect(() => {
    if (!open) {
      setTitle("");
      setDescription("");
      setDocumentType("quyet_dinh");
      setDocumentNumber("");
      setIssuedDate(todayISO());
      setEffectiveUntil("");
      setUrgency("normal");
      setIssuingDepartment("");
      setSignerRole("COO");
      setFile(null);
      setDragOver(false);
      setSubmitting(false);
      setSubmitMode(null);
      dragDepthRef.current = 0;
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [open]);

  const acceptFile = (next) => {
    if (!next) return;
    if (!isPdfFile(next)) {
      showErrorToast("Chỉ chấp nhận file PDF.");
      return;
    }
    if (next.size > 25 * 1024 * 1024) {
      showErrorToast("File vượt quá 25MB.");
      return;
    }
    setFile(next);
  };

  const clearFile = () => {
    setFile(null);
    setDragOver(false);
    dragDepthRef.current = 0;
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current += 1;
    setDragOver(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current = 0;
    setDragOver(false);
    const dropped = e.dataTransfer?.files?.[0];
    acceptFile(dropped);
  };

  const validateForm = () => {
    if (!title.trim()) {
      showErrorToast("Vui lòng nhập tiêu đề văn bản.");
      return false;
    }
    if (!documentType) {
      showErrorToast("Vui lòng chọn loại văn bản.");
      return false;
    }
    if (!issuedDate) {
      showErrorToast("Vui lòng chọn ngày ban hành.");
      return false;
    }
    if (effectiveUntil && effectiveUntil < issuedDate) {
      showErrorToast("Ngày hết hiệu lực không được trước ngày ban hành.");
      return false;
    }
    if (!file) {
      showErrorToast("Vui lòng chọn file PDF.");
      return false;
    }
    return true;
  };

  const buildFormData = () => {
    const fd = new FormData();
    fd.append("title", title.trim());
    fd.append("description", description.trim());
    fd.append("document_type", documentType);
    if (documentNumber.trim()) fd.append("document_number", documentNumber.trim());
    fd.append("issued_date", issuedDate);
    if (effectiveUntil) fd.append("effective_until", effectiveUntil);
    fd.append("urgency", urgency);
    if (issuingDepartment.trim()) fd.append("issuing_department", issuingDepartment.trim());
    fd.append("signer_role", signerRole);
    fd.append("file", file);
    return fd;
  };

  const handleSave = async (mode) => {
    if (submitting || !validateForm()) return;
    setSubmitting(true);
    setSubmitMode(mode);
    try {
      const created = await createSigningRequest(buildFormData());
      if (mode === "submit") {
        try {
          const submitted = await submitSigningRequest(created.id);
          showSuccessToast("Đã trình ký.");
          onCreated?.(submitted);
        } catch (submitErr) {
          showErrorToast(
            submitErr?.response?.data?.message ||
              "Đã lưu nháp nhưng trình ký thất bại. Mở chi tiết để trình lại."
          );
          onCreated?.(created);
        }
      } else {
        showSuccessToast("Đã lưu nháp.");
        onCreated?.(created);
      }
      onClose?.();
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Lỗi khi tạo yêu cầu.");
    } finally {
      setSubmitting(false);
      setSubmitMode(null);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
  };

  const selectedSigner = SIGNER_ROLE_OPTIONS.find((o) => o.value === signerRole);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[80] bg-black/40 backdrop-blur-sm"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 24 }}
            transition={{ duration: 0.28, ease: "easeOut" }}
            className="fixed inset-0 z-[90] flex items-center justify-center p-4 pointer-events-none"
          >
            <div
              className="pointer-events-auto bg-white rounded-[1.75rem] shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col border border-gray-100"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-red-50/60 via-white to-white shrink-0">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-red-600/80 mb-1">
                      HRM · Trình ký
                    </p>
                    <h2 className="text-xl font-black uppercase tracking-tight text-gray-800 flex items-center gap-2">
                      <FileSignature className="h-6 w-6 text-red-700 shrink-0" />
                      Tạo yêu cầu trình ký
                    </h2>
                    <p className="text-xs text-gray-500 mt-1 max-w-lg">
                      Cột trái: thông tin văn bản · Cột phải: file PDF và luồng trình ký.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    className="p-2 rounded-xl hover:bg-white/80 text-gray-500 shrink-0"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              <form
                onSubmit={handleSubmit}
                className="flex-1 min-h-0 flex flex-col"
              >
                <div className="flex-1 overflow-y-auto p-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
                    {/* Cột 1 — Thông tin văn bản */}
                    <section className={sectionClass}>
                      <h3 className="text-xs font-black uppercase tracking-widest text-gray-700 flex items-center gap-2">
                        <FileText size={14} className="text-red-600" />
                        Thông tin văn bản
                      </h3>

                      <div>
                        <label className={labelClass}>
                          Tiêu đề văn bản <span className="text-red-500">*</span>
                        </label>
                        <input
                          value={title}
                          onChange={(e) => setTitle(e.target.value)}
                          className={inputClass}
                          placeholder="VD: Quyết định bổ nhiệm / Công văn thông báo..."
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className={labelClass}>
                            Loại văn bản <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={documentType}
                            onChange={(e) => setDocumentType(e.target.value)}
                            className={inputClass}
                          >
                            {DOCUMENT_TYPE_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className={labelClass}>
                            <Hash size={12} /> Số hiệu / Số văn bản
                          </label>
                          <input
                            value={documentNumber}
                            onChange={(e) => setDocumentNumber(e.target.value)}
                            className={inputClass}
                            placeholder="VD: 01/QĐ-HN hoặc CV-2025..."
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className={labelClass}>
                            <Calendar size={12} /> Ngày ban hành{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="date"
                            value={issuedDate}
                            onChange={(e) => setIssuedDate(e.target.value)}
                            className={inputClass}
                          />
                        </div>
                        <div>
                          <label className={labelClass}>
                            <Calendar size={12} /> Ngày hết hiệu lực
                          </label>
                          <input
                            type="date"
                            value={effectiveUntil}
                            min={issuedDate || undefined}
                            onChange={(e) => setEffectiveUntil(e.target.value)}
                            className={inputClass}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className={labelClass}>
                            <Building2 size={12} /> Phòng ban ban hành
                          </label>
                          <input
                            value={issuingDepartment}
                            onChange={(e) => setIssuingDepartment(e.target.value)}
                            className={inputClass}
                            placeholder="VD: Phòng HCNS, Ban Giám đốc..."
                          />
                        </div>
                        <div>
                          <label className={labelClass}>
                            <AlertCircle size={12} /> Mức độ khẩn
                          </label>
                          <select
                            value={urgency}
                            onChange={(e) => setUrgency(e.target.value)}
                            className={inputClass}
                          >
                            {URGENCY_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className={labelClass}>Nội dung / Ghi chú</label>
                        <textarea
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          rows={4}
                          className={inputClass}
                          placeholder="Tóm tắt nội dung, phạm vi áp dụng hoặc ghi chú cho người ký..."
                        />
                      </div>
                    </section>

                    {/* Cột 2 — PDF + Luồng trình ký */}
                    <div className="space-y-5 lg:sticky lg:top-0">
                      <section className={sectionClass}>
                        <h3 className="text-xs font-black uppercase tracking-widest text-gray-700 flex items-center gap-2">
                          <Upload size={14} className="text-red-600" />
                          File PDF văn bản <span className="text-red-500">*</span>
                        </h3>

                        {file ? (
                          <div
                            className={`rounded-2xl border bg-white p-4 transition-colors ${
                              dragOver
                                ? "border-red-400 border-dashed ring-2 ring-red-200 bg-red-50/60"
                                : "border-gray-200"
                            }`}
                            onDragEnter={handleDragEnter}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                          >
                            <div className="flex items-start gap-3">
                              <div className="h-11 w-11 rounded-xl bg-red-50 border border-red-100 flex items-center justify-center text-red-600 shrink-0">
                                <FileText size={20} />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-semibold text-gray-800 truncate">
                                  {file.name}
                                </p>
                                <p className="text-xs text-gray-400 mt-1">
                                  {(file.size / 1024 / 1024).toFixed(2)} MB
                                </p>
                                {dragOver ? (
                                  <p className="mt-2 text-xs font-semibold text-red-600">
                                    Thả PDF để thay thế…
                                  </p>
                                ) : (
                                  <p className="mt-2 text-xs text-gray-400">
                                    Kéo thả PDF khác để thay thế
                                  </p>
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={clearFile}
                                className="inline-flex items-center justify-center h-10 w-10 rounded-xl text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors shrink-0"
                                title="Xóa file"
                              >
                                <Trash2 size={18} />
                              </button>
                            </div>
                            <button
                              type="button"
                              onClick={() => fileInputRef.current?.click()}
                              className="mt-3 text-xs font-semibold text-red-600 hover:underline"
                            >
                              Đổi file khác
                            </button>
                          </div>
                        ) : (
                          <div
                            role="button"
                            tabIndex={0}
                            onClick={() => fileInputRef.current?.click()}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                fileInputRef.current?.click();
                              }
                            }}
                            className={`flex flex-col items-center justify-center gap-3 border-2 border-dashed rounded-2xl p-8 cursor-pointer transition-all text-center outline-none focus:ring-4 focus:ring-red-500/10 ${
                              dragOver
                                ? "border-red-500 bg-red-50 ring-2 ring-red-200"
                                : "border-gray-200 hover:border-red-300 hover:bg-red-50/20"
                            }`}
                            onDragEnter={handleDragEnter}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                          >
                            <Upload
                              className={dragOver ? "text-red-600" : "text-gray-400"}
                              size={32}
                            />
                            <div>
                              <p className="text-sm font-semibold text-gray-700">
                                {dragOver
                                  ? "Thả file PDF vào đây"
                                  : "Kéo thả hoặc bấm để chọn PDF"}
                              </p>
                              <p className="text-xs text-gray-400 mt-1">
                                Tối đa 25MB · Chỉ chấp nhận file PDF
                              </p>
                            </div>
                          </div>
                        )}

                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="application/pdf,.pdf"
                          className="hidden"
                          onChange={(e) => {
                            acceptFile(e.target.files?.[0] || null);
                            e.target.value = "";
                          }}
                        />
                      </section>

                      <section className={sectionClass}>
                        <h3 className="text-xs font-black uppercase tracking-widest text-gray-700 flex items-center gap-2">
                          <PenLine size={14} className="text-red-600" />
                          Luồng trình ký
                        </h3>
                        <div className="space-y-3">
                          {SIGNER_ROLE_OPTIONS.map((opt) => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => setSignerRole(opt.value)}
                              className={`w-full text-left rounded-2xl border p-4 transition-all ${
                                signerRole === opt.value
                                  ? "border-red-300 bg-red-50/50 ring-2 ring-red-500/10"
                                  : "border-gray-200 bg-white hover:border-gray-300"
                              }`}
                            >
                              <p className="text-sm font-bold text-gray-800">
                                {opt.label}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">{opt.hint}</p>
                            </button>
                          ))}
                        </div>
                        {selectedSigner && (
                          <p className="text-[11px] text-gray-400 italic">
                            Đã chọn: {selectedSigner.label}
                          </p>
                        )}
                      </section>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 justify-end px-6 py-4 border-t border-gray-100 bg-white shrink-0">
                  <button
                    type="button"
                    disabled={submitting}
                    onClick={() => handleSave("draft")}
                    className="inline-flex items-center gap-1.5 px-5 py-2.5 text-xs font-bold uppercase tracking-wider rounded-full border border-gray-200 text-gray-700 hover:border-red-300 hover:text-red-600 bg-white disabled:opacity-50 transition-all"
                  >
                    <Save size={14} />
                    {submitting && submitMode === "draft" ? "Đang lưu..." : "Lưu nháp"}
                  </button>
                  <button
                    type="button"
                    disabled={submitting}
                    onClick={() => handleSave("submit")}
                    className="inline-flex items-center gap-1.5 px-8 py-2.5 bg-gradient-to-r from-red-700 to-red-800 text-white rounded-full font-bold uppercase text-xs tracking-wider hover:shadow-lg hover:shadow-red-100 disabled:opacity-50 transition-all"
                  >
                    <Send size={14} />
                    {submitting && submitMode === "submit" ? "Đang trình..." : "Trình ký"}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SigningRequestCreateModal;
